import Button from "@ui/button/Button"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { systemErrorDetails, systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { T } from "../adminTheme"
import { AdminModal } from "./AdminModal"
import { Callout, date, Fields, Muted, Panel, Row, State, StatusBadge, Table } from "./adminUi"
import { MoveUserModal, RoleTeamFields, useCompanyTeams, type CompanyRole, type MovePrefill } from "./UserCompanyMove"

// Pending invites of a company (Superadmin_Invites_Links_FE.md). `signup_url` and `token` let anyone create an
// account, so they are dropped on load and only ever travel from the resend response straight to the clipboard.

const INVITE_GONE = 2800
/** The e-mail already has an account elsewhere: an invite can never be accepted, the user must be moved. */
const EMAIL_REGISTERED = 2805
const RESENDABLE = ["sent", "opened", "error"]

interface IInvite {
	id: number
	status: string
	role?: string
	name?: string
	email: string
	team_name?: string
	expires_at?: string
	expired?: boolean
}

interface IResend {
	signup_url?: string
	email_sent?: boolean
	email_error?: string
}

export const CompanyInvites = ({ token, companyUid }: { token: string; companyUid: string }) => {
	const [creating, setCreating] = useState(false)
	const [invites, setInvites] = useState<IInvite[] | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [busyId, setBusyId] = useState<number | null>(null)
	const [mailErrors, setMailErrors] = useState<Record<number, string>>({})
	const requestId = useRef(0)

	const load = useCallback(async () => {
		const id = ++requestId.current
		const response = await systemPost<IInvite[]>(token, "system/invites/list", { company_uid: companyUid })
		if (id !== requestId.current) return
		if (response.error) return setError(systemErrorMessage(response.error))
		setError(null)
		setInvites((Array.isArray(response.data) ? response.data : [])
			.filter((invite) => invite.status !== "used")
			.map(({ id: inviteId, status, role, name, email, team_name, expires_at, expired }) => ({ id: inviteId, status, role, name, email, team_name, expires_at, expired })))
	}, [token, companyUid])

	useEffect(() => {
		setInvites(null)
		void load()
		return () => { requestId.current++ }
	}, [load])

	const resend = async (invite: IInvite, sendEmail: boolean) => {
		setBusyId(invite.id)
		try {
			const response = await systemPost<IResend>(token, "system/invites/resend", sendEmail ? { id: invite.id } : { id: invite.id, send_email: false })
			if (response.error) {
				// The backend gives no hint here; the most common reason is a finished signup.
				toast.error(Number(response.error.code) === INVITE_GONE ? "Invite already used: the user signed up with it, or it was deleted" : systemErrorMessage(response.error))
				// 2800: already used or deleted. Either way the list is stale.
				if (Number(response.error.code) === INVITE_GONE) await load()
				return
			}
			setMailErrors(({ [invite.id]: _dropped, ...rest }) => rest)
			if (sendEmail) {
				if (response.data.email_sent) toast.success(`Email sent to ${invite.email}`)
				else setMailErrors((current) => ({ ...current, [invite.id]: response.data.email_error || "The mail service did not accept the message" }))
			} else {
				try {
					await navigator.clipboard.writeText(response.data.signup_url ?? "")
					toast.success("Link copied. The invite is valid for 7 more days")
				} catch {
					toast.error("Could not copy the link. Allow clipboard access and try again")
				}
			}
			await load()
		} finally {
			setBusyId(null)
		}
	}

	return <Panel title='Invites' hint='People invited to this company who have not finished signing up.' actions={<Button variant='primarySmall' onClick={() => setCreating(true)}>Send invite</Button>}>
		<State loading={invites === null && !error} error={error} empty={invites?.length === 0} emptyText='No pending invites. Never invited — send a new one. Signed up on their own — move them here from their profile.' />
		{invites && invites.length > 0 && <Table>
			<thead><tr><th>Invitee</th><th>Role</th><th>Team</th><th>Status</th><th>Expires</th><th /></tr></thead>
			<tbody>{invites.map((invite) => <tr key={invite.id}>
				<td><span style={{ fontWeight: 600 }}>{invite.name || "—"}</span><small>{invite.email}</small></td>
				<td style={{ textTransform: "capitalize" }}>{invite.role || "—"}</td>
				<td>{invite.team_name || "—"}</td>
				<td><Row style={{ gap: 8 }}><StatusBadge value={invite.status} />{invite.expired && <StatusBadge value='expired' />}</Row></td>
				<td>{date(invite.expires_at)}</td>
				<td>
					{RESENDABLE.includes(invite.status) && <Row style={{ justifyContent: "flex-end", flexWrap: "nowrap" }}>
						<Button variant='secondarySmall' disabled={busyId !== null} onClick={() => void resend(invite, false)}>Copy link</Button>
						<Button variant='secondarySmall' disabled={busyId !== null} onClick={() => void resend(invite, true)}>Resend email</Button>
					</Row>}
					{mailErrors[invite.id] !== undefined && <Muted style={{ display: "block", marginTop: 6, textAlign: "right", color: T.danger, fontSize: 12.5 }}>Email not sent: {mailErrors[invite.id]}. Use Copy link and send it yourself.</Muted>}
				</td>
			</tr>)}</tbody>
		</Table>}
		{creating && <InviteCreateModal token={token} companyUid={companyUid} onClose={() => setCreating(false)} onSent={load} />}
	</Panel>
}

interface ICreated { email: string; company_name?: string; invited_by_email?: string; signup_url?: string }
interface IFoundUser { uid: string; email?: string; name?: string; company_name?: string }

/** Superadmin_User_Move_Invite_FE.md §2: the cabinet invite, sent on behalf of an active admin of this company. */
const InviteCreateModal = ({ token, companyUid, onClose, onSent }: { token: string; companyUid: string; onClose: () => void; onSent: () => Promise<void> }) => {
	const [email, setEmail] = useState("")
	const [name, setName] = useState("")
	const [role, setRole] = useState<CompanyRole>("buyer")
	const [teamUid, setTeamUid] = useState("")
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<{ text: string; registered: boolean } | null>(null)
	const [sent, setSent] = useState<ICreated | null>(null)
	const [move, setMove] = useState<{ user: IFoundUser; prefill: MovePrefill } | null>(null)
	const teams = useCompanyTeams(token, companyUid)
	const value = email.trim().toLowerCase()
	const ready = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && name.trim().length >= 2 && (role !== "buyer" || !!teamUid)

	const send = async () => {
		setBusy(true); setError(null)
		const response = await systemPost<ICreated>(token, "system/invites/create", { company_uid: companyUid, email: value, name: name.trim(), role, ...(teamUid ? { team_uid: teamUid } : {}) })
		setBusy(false)
		if (response.error) return setError({ text: systemErrorDetails(response.error), registered: Number(response.error.code) === EMAIL_REGISTERED })
		setSent(response.data)
		await onSent()
	}
	const findUser = async () => {
		setBusy(true)
		const response = await systemPost<IFoundUser[] | { rows?: IFoundUser[] }>(token, "system/user/list", { search: value, limit: 8 })
		setBusy(false)
		const found = (Array.isArray(response.data) ? response.data : response.data?.rows ?? []).find((user) => user.email?.toLowerCase() === value)
		if (!found) return toast.error(response.error ? systemErrorMessage(response.error) : `No user with e-mail ${value} found`)
		setMove({ user: found, prefill: { companyUid, role, teamUid } })
	}
	const copy = async () => {
		try {
			await navigator.clipboard.writeText(sent?.signup_url ?? "")
			toast.success("Link copied")
		} catch {
			toast.error("Could not copy the link. Allow clipboard access and try again")
		}
	}

	if (move) return <MoveUserModal token={token} userUid={move.user.uid} userLabel={move.user.name || move.user.email || move.user.uid} fromCompany={move.user.company_name ?? ""} prefill={move.prefill} onClose={onClose} onMoved={onSent} />
	return <AdminModal title='Send invite' subtitle={`Company ${companyUid}`} width={560} onClose={onClose}>
		{sent
			? <>
				<Callout tone='success' title={`Invite sent to ${sent.email}`}>
					On behalf of {sent.invited_by_email || "the company admin"}. A new invite to the same e-mail replaces this one.
				</Callout>
				<Row style={{ justifyContent: "flex-end" }}>
					{sent.signup_url && <Button variant='secondarySmall' onClick={() => void copy()}>Copy link</Button>}
					<Button variant='primarySmall' onClick={onClose}>Done</Button>
				</Row>
			</>
			: <>
				<Fields style={{ gridTemplateColumns: "1fr" }}>
					<label>E-mail<input type='email' autoComplete='off' value={email} onChange={(event) => { setEmail(event.target.value); setError(null) }} /></label>
					<label>Full name<input autoComplete='off' maxLength={100} value={name} onChange={(event) => setName(event.target.value)} /></label>
					<RoleTeamFields teams={teams} role={role} teamUid={teamUid} companyChosen onRole={setRole} onTeam={setTeamUid} />
				</Fields>
				{error && <Callout tone='danger' title={error.registered ? "The client signed up on their own — move them into this company instead." : undefined}>
					{error.text}
					{error.registered && <Row style={{ marginTop: 6 }}><Button variant='dangerSmall' disabled={busy} onClick={() => void findUser()}>Move this user to the company</Button></Row>}
				</Callout>}
				<Row style={{ justifyContent: "flex-end" }}>
					<Button variant='secondarySmall' onClick={onClose}>Cancel</Button>
					<Button variant='primarySmall' disabled={!ready || busy} onClick={() => void send()}>{busy ? "Sending…" : "Send invite"}</Button>
				</Row>
			</>}
	</AdminModal>
}
